import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate, type Identifier } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

import { StatusPill } from "../design/StatusPill";
import { MyAvailability } from "../leadAssignment/MyAvailability";
import { formatBlockTime } from "../leadAssignment/availabilityBlocks";
import type { CrmDataProvider } from "../providers/types";
import type { ProjectGuardiaRosterRow } from "../types";
import { GuardiasRosterForm } from "./GuardiasRosterForm";
import {
  addDays,
  groupRosterByDate,
  mexicoToday,
  mondayOf,
  onGuardiaNames,
  weekDates,
  weekdayOf,
} from "./guardiasRules";
import {
  GUARDIAS_KEY,
  HELP_CLASS,
  QueryBoundary,
  SectionCard,
  useGuardiasSave,
  type GuardiasSectionProps,
  type SectionAdvisor,
} from "./guardiasShared";

type RosterProps = GuardiasSectionProps & {
  advisors: SectionAdvisor[];
  /** The signed-in advisor, to find her own guardias and to say "you are on guardia". */
  ownId: Identifier | undefined;
};

/**
 * "En la sala de ventas ahora" (derived from the roster and the office hours, never from
 * tracking) plus the weekly guardia roster: the Team Leader builds it from the availability each
 * advisor marked; the asesora sees HER guardias and the week's roster of her project.
 */
export const GuardiasRoster = ({
  projectId,
  canEdit,
  advisors,
  ownId,
}: RosterProps) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { saving, run } = useGuardiasSave();
  const [monday, setMonday] = useState(() => mondayOf(mexicoToday()));
  const dates = weekDates(monday);

  const now = useQuery({
    queryKey: [GUARDIAS_KEY, projectId, "now"],
    queryFn: () => dataProvider.getProjectOnGuardiaNow(projectId),
  });
  const roster = useQuery({
    queryKey: [GUARDIAS_KEY, projectId, "roster", monday],
    queryFn: () =>
      dataProvider.getProjectGuardiaRoster(projectId, monday, dates[4]),
  });

  const endGuardia = (id: Identifier) =>
    run(() =>
      dataProvider.update("project_guardias", {
        id,
        data: { ended_at: new Date().toISOString() },
        previousData: { id },
      }),
    );

  const weekday = (date: string) =>
    translate(`resources.guardias.weekday.${weekdayOf(date)}`);
  const range = (row: ProjectGuardiaRosterRow) =>
    translate("resources.guardias.guardia_range", {
      start: formatBlockTime(row.start_time),
      end: formatBlockTime(row.end_time),
    });
  const fullName = (row: ProjectGuardiaRosterRow) =>
    `${row.first_name} ${row.last_name}`.trim();

  return (
    <>
      <SectionCard title={translate("resources.guardias.now_title")}>
        <QueryBoundary query={now}>
          {(entries) => {
            const names = onGuardiaNames(entries);
            const isOwnOnGuardia = entries.some(
              (entry) =>
                ownId != null && String(entry.sales_id) === String(ownId),
            );
            return names.length === 0 ? (
              <StatusPill
                tone="not-ok"
                text={translate("resources.guardias.now_empty")}
              />
            ) : (
              <div className="space-y-2" data-testid="guardias-now">
                <ul className="space-y-1 text-base">
                  {names.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                {isOwnOnGuardia ? (
                  <StatusPill
                    tone="ok"
                    text={translate("resources.guardias.now_you")}
                  />
                ) : null}
              </div>
            );
          }}
        </QueryBoundary>
      </SectionCard>

      <SectionCard
        title={translate("resources.guardias.roster_title")}
        help={translate("resources.guardias.roster_help")}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={() => setMonday(addDays(monday, -7))}
          >
            {translate("resources.guardias.week_prev")}
          </Button>
          <p className="text-base font-medium tabular-nums">
            {translate("resources.guardias.week_range", {
              from: dates[0],
              to: dates[4],
            })}
          </p>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={() => setMonday(addDays(monday, 7))}
          >
            {translate("resources.guardias.week_next")}
          </Button>
        </div>

        <QueryBoundary query={roster}>
          {(rows) => {
            const byDate = groupRosterByDate(rows, dates);
            const mine = rows.filter(
              (row) => ownId != null && String(row.sales_id) === String(ownId),
            );
            return (
              <div className="space-y-4">
                {!canEdit ? (
                  <div className="space-y-1">
                    <h3 className="text-base font-semibold">
                      {translate("resources.guardias.mine_title")}
                    </h3>
                    {mine.length === 0 ? (
                      <p className={HELP_CLASS}>
                        {translate("resources.guardias.mine_empty")}
                      </p>
                    ) : (
                      <ul className="space-y-1 text-base tabular-nums">
                        {mine.map((row) => (
                          <li key={row.id}>
                            {weekday(row.guardia_date)} {row.guardia_date}{" "}
                            {range(row)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}

                <ul className="divide-y" data-testid="guardias-roster">
                  {dates.map((date) => (
                    <li key={date} className="space-y-1 py-3">
                      <h3 className="text-base font-semibold">
                        {weekday(date)}{" "}
                        <span className="font-normal tabular-nums text-muted-foreground">
                          {date}
                        </span>
                      </h3>
                      {byDate[date].length === 0 ? (
                        <p className={HELP_CLASS}>
                          {translate("resources.guardias.day_empty")}
                        </p>
                      ) : (
                        <ul className="space-y-1">
                          {byDate[date].map((row) => (
                            <li
                              key={row.id}
                              className="flex min-h-11 items-center justify-between gap-3 text-base"
                            >
                              <span className="tabular-nums">
                                {fullName(row)} · {range(row)}
                              </span>
                              {canEdit ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="h-11"
                                  disabled={saving}
                                  aria-label={translate(
                                    "resources.guardias.guardia_end_aria",
                                    {
                                      name: fullName(row),
                                      day: weekday(date),
                                      start: formatBlockTime(row.start_time),
                                    },
                                  )}
                                  onClick={() => endGuardia(row.id)}
                                >
                                  {translate("resources.guardias.guardia_end")}
                                </Button>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }}
        </QueryBoundary>
      </SectionCard>

      {canEdit ? (
        <GuardiasRosterForm
          projectId={projectId}
          advisors={advisors}
          dates={dates}
          ownId={ownId}
        />
      ) : (
        <SectionCard
          title={translate("resources.guardias.availability_link")}
          help={translate("resources.guardias.availability_help")}
        >
          <Button asChild className="h-11">
            <Link to={MyAvailability.path}>
              {translate("resources.guardias.availability_link")}
            </Link>
          </Button>
        </SectionCard>
      )}
    </>
  );
};
